/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type {
  Carousels,
  TSmartCarouselLiveTvGetParams
} from "@tivocorp/data-domain-lib";

import { PostSigninInteractor } from "../../model/interactor";
import { DiagnosticLogger, Log, OperationalLogger } from "../../model/logging";
import { RetryMechanism } from "../../model/retrymechanism/RetryMechanism";
import { DiagnosticSeverity, RETRY_POLICY } from "../../model/types";
import type { TCarouselItem, TCarouselRail } from "../../model/types";
import {
  applyOffsetPage,
  CAROUSEL_TILE_PAGE_SIZE
} from "../utility/carouselPagination";
import {
  createLoadMoreTilesThunk,
  TLoadMoreTilesPayload
} from "../utility/carouselThunkFactory";
import type {
  TCarouselRailPage,
  TCarouselRailPaginationState,
  TTilePaginationState
} from "../utility/carouselPagination";
import type { UnityRootState, UnityAppDispatch } from "../UnityAppStore";
import {
  mapCarouselsResponseToInitialTilePagination,
  mapCarouselsResponseToPage
} from "../homescreen/homescreenMappers";
import { INITIAL_STATE } from "./playerCarouselTypes";
import type { PlayerCarouselState } from "./playerCarouselTypes";
import { DEFAULT_CAROUSELS_REFRESH_INTERVAL_SECS } from "../../config";
import {
  getImageHostBaseUrl,
  buildLiveTvGetParams,
  fetchCarouselTilePage,
  handleTileSelectionPlayback
} from "../utility/smartCarouselUtility";
import {
  PLAYER_CAROUSEL_API_ENDPOINT,
  PLAYER_CAROUSEL_SCREEN_ID
} from "./playerCarouselConstants";
import { PlayerCarouselFocusedField } from "./playerCarouselFocusedField";
import { playableUpdated } from "../videoPlayerUi/videoPlayerUiSlice";
import { DeviceModel } from "../../model/device";

const TAG: string = "playerCarouselSlice";
// TODO: Remove this message once we get proper error message from UX
const UNKNOWN_ERROR_MESSAGE: string = "Unknown error";

/** Operational logger bound to the Live TV carousel endpoint. */
const PlayerCarouselOperationalLogger = OperationalLogger.create({
  screenId: PLAYER_CAROUSEL_SCREEN_ID,
  apiEndpoint: PLAYER_CAROUSEL_API_ENDPOINT
});

/** Diagnostic event name for the player carousel lifecycle. */
const DIAGNOSTIC_EVENT: string = "player_carousel";

// ─── Async Thunks ───

/**
 * Starts playback for a selected player-carousel tile.
 * For Live content: fetches channel info by stationId and starts live playback.
 * TODO: For future content or non-Live offer types: no-ops (logs only).
 */
export const startPlayerCarouselTilePlayback = createAsyncThunk<
  boolean,
  TCarouselItem,
  { rejectValue: string }
>(
  "playerCarousel/startTilePlayback",
  async (feedItem: TCarouselItem, { rejectWithValue }) => {
    try {
      return await handleTileSelectionPlayback(
        feedItem,
        "startPlayerCarouselTilePlayback()"
      );
    } catch (error: unknown) {
      const message: string =
        error instanceof Error
          ? error.message
          : "Failed to start tile playback";
      Log.e(TAG, "startPlayerCarouselTilePlayback()", error);
      return rejectWithValue(message);
    }
  }
);

interface InitialPlayerCarouselPayload {
  page: TCarouselRailPage;
  tilePagination: Record<string, TTilePaginationState>;
  refreshIntervalSecs: number;
}

const mapScreenResponse = (
  response: Carousels,
  imageHostBaseUrl: string
): InitialPlayerCarouselPayload => ({
  page: mapCarouselsResponseToPage(response, imageHostBaseUrl),
  tilePagination: mapCarouselsResponseToInitialTilePagination(
    response,
    imageHostBaseUrl
  ),
  refreshIntervalSecs:
    response.refreshIntervalSecs === undefined ||
    response.refreshIntervalSecs === null
      ? DEFAULT_CAROUSELS_REFRESH_INTERVAL_SECS
      : (response.refreshIntervalSecs as number)
});

const fetchLiveTv = async (
  offset: number = 0,
  fetchOptions?: { signal?: AbortSignal }
): Promise<InitialPlayerCarouselPayload> => {
  const interactor: PostSigninInteractor = new PostSigninInteractor();
  const imageHostBaseUrl: string = await getImageHostBaseUrl(interactor);
  const params: TSmartCarouselLiveTvGetParams = await buildLiveTvGetParams(
    offset,
    CAROUSEL_TILE_PAGE_SIZE
  );
  const response = await interactor.fetchLiveTv(params, fetchOptions);
  return mapScreenResponse(response, imageHostBaseUrl);
};

/**
 * Wraps the player-screen recommendations API call with CRITICAL retry.
 * Request failures retry silently in the background using RetryMechanism
 * with CRITICAL policy.
 *
 * The caller must provide an AbortSignal so the retry loop can be
 * cancelled when the player is dismissed.
 */
export const loadPlayerCarouselWithRetry = createAsyncThunk<
  InitialPlayerCarouselPayload,
  { signal: AbortSignal },
  { rejectValue: { message: string } }
>(
  "playerCarousel/loadWithRetry",
  async ({ signal }: { signal: AbortSignal }, { rejectWithValue }) => {
    // Player carousel is SmartTV-only — skip on browser.
    if (!DeviceModel.isTvDevice()) {
      Log.i(TAG, "loadPlayerCarouselWithRetry() - skipped on browser");
      return rejectWithValue({ message: "" });
    }

    const fetchLiveTvFn = (): Promise<InitialPlayerCarouselPayload> =>
      fetchLiveTv(0, { signal });

    const rejectIfAborted = (context: string): { message: string } | null => {
      if (!signal.aborted) {
        return null;
      }
      Log.d(TAG, `loadPlayerCarouselWithRetry() - ${context}`);
      return { message: "" };
    };

    const fetchStartTime: number = Date.now();
    try {
      Log.i(TAG, "loadPlayerCarouselWithRetry() - Initial attempt");
      const result = await fetchLiveTvFn();
      const fetchDuration = Date.now() - fetchStartTime;
      Log.d(
        TAG,
        `loadPlayerCarouselWithRetry() - loaded rails=${result.page.items.length}, durationMs=${fetchDuration}`
      );
      DiagnosticLogger.log(
        DIAGNOSTIC_EVENT,
        "Initial live TV carousel load succeeded",
        DiagnosticSeverity.Debug,
        {
          screen_id: PLAYER_CAROUSEL_SCREEN_ID,
          event: "initial_load_succeeded",
          rail_count: String(result.page.items.length),
          latency_ms: String(fetchDuration)
        }
      );

      return result;
    } catch (firstError: unknown) {
      const abortPayload = rejectIfAborted("Aborted (intentional)");
      if (abortPayload !== null) {
        return rejectWithValue(abortPayload);
      }
      Log.w(
        TAG,
        "loadPlayerCarouselWithRetry() - Initial fetch failed, retrying",
        firstError
      );
      PlayerCarouselOperationalLogger.logApiFailure(firstError, {
        latencyMs: Date.now() - fetchStartTime,
        extra: { attempt: "initial" }
      });
    }

    Log.i(
      TAG,
      "loadPlayerCarouselWithRetry() - Server error, starting CRITICAL retry"
    );
    const retryStartTime: number = Date.now();
    try {
      const retryResult =
        await RetryMechanism.callServiceWithRetry<InitialPlayerCarouselPayload>(
          fetchLiveTvFn as (
            ...args: Array<unknown>
          ) => Promise<InitialPlayerCarouselPayload>,
          [],
          { retryPolicy: RETRY_POLICY.CRITICAL, httpMethod: "GET" },
          signal
        );
      const retryDuration = Date.now() - retryStartTime;
      const retryRails = retryResult.page.items;
      Log.d(
        TAG,
        `loadPlayerCarouselWithRetry() - retry rails=${retryRails.length}, durationMs=${retryDuration}`
      );
      DiagnosticLogger.log(
        DIAGNOSTIC_EVENT,
        "CRITICAL retry recovered the live TV carousel",
        DiagnosticSeverity.Debug,
        {
          screen_id: PLAYER_CAROUSEL_SCREEN_ID,
          event: "retry_succeeded",
          rail_count: String(retryRails.length),
          latency_ms: String(retryDuration)
        }
      );

      return retryResult;
    } catch (retryError: unknown) {
      const abortPayload = rejectIfAborted("Retry aborted (intentional)");
      if (abortPayload !== null) {
        return rejectWithValue(abortPayload);
      }
      const message: string =
        retryError instanceof Error
          ? retryError.message
          : "Failed to load player carousels";
      Log.e(
        TAG,
        "loadPlayerCarouselWithRetry() - Retry exhausted/aborted",
        retryError
      );
      PlayerCarouselOperationalLogger.logApiFailure(retryError, {
        latencyMs: Date.now() - retryStartTime,
        extra: { attempt: "retry" }
      });
      return rejectWithValue({ message });
    }
  }
);

export const loadMorePlayerCarouselTiles = createLoadMoreTilesThunk({
  thunkPrefix: "playerCarousel",
  tag: TAG,
  getTileState: (state: UnityRootState, carouselId: string) =>
    state.playerCarousel.tilePagination[carouselId],
  fetchPage: fetchCarouselTilePage
});

/**
 * TTL-guarded refresh thunk.
 *
 * When `isProgramBoundary` is true the TTL check is bypassed — the
 * program-boundary timer is authoritative and should always trigger a fetch.
 * Otherwise the thunk skips the request if data is still fresh or a fetch
 * is already in flight.
 *
 * Delegates to loadPlayerCarouselWithRetry so request failures are retried
 * with CRITICAL policy.
 */
export const refreshPlayerCarousel =
  (signal: AbortSignal, isProgramBoundary: boolean = false) =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    const { playerCarousel } = getState();
    const elapsed: number = Date.now() - playerCarousel.lastFetchTimestamp;
    const ttl: number = playerCarousel.refreshIntervalSecs * 1000;

    if (
      !isProgramBoundary &&
      (playerCarousel.carouselsPagination.isLoading || elapsed < ttl)
    ) {
      Log.i(
        TAG,
        "refreshPlayerCarousel() - data still fresh or loading, skipping"
      );
      return;
    }

    Log.i(
      TAG,
      "refreshPlayerCarousel() - data stale, dispatching loadPlayerCarouselWithRetry"
    );
    dispatch(loadPlayerCarouselWithRetry({ signal }));
  };

// ─── Slice ───

const playerCarouselSlice = createSlice({
  name: "playerCarousel",
  initialState: INITIAL_STATE,
  reducers: {
    setFocusedPlayerCarouselIndex(state, action: PayloadAction<number>): void {
      state.focusedCarouselIndex = action.payload;
    },
    setFocusedPlayerCarouselTileIndex(
      state,
      action: PayloadAction<{ carouselIndex: number; tileIndex: number }>
    ): void {
      state.focusedTileIndices[action.payload.carouselIndex] =
        action.payload.tileIndex;
    },
    clearPlayerCarouselError(state): void {
      state.carouselsPagination.error = "";
    },
    playerCarouselReset(): PlayerCarouselState {
      return INITIAL_STATE;
    },
    setPlayerCarouselFocusedField(
      state,
      action: PayloadAction<PlayerCarouselFocusedField>
    ): void {
      state.focusedField = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Initial load + CRITICAL retry
    builder
      .addCase(loadPlayerCarouselWithRetry.pending, (state): void => {
        state.carouselsPagination.isLoading = true;
        state.carouselsPagination.error = "";
      })
      .addCase(
        loadPlayerCarouselWithRetry.fulfilled,
        (state, action: PayloadAction<InitialPlayerCarouselPayload>): void => {
          state.carouselsPagination.items = action.payload.page.items;
          state.carouselsPagination.pageCursor = action.payload.page.pageCursor;
          state.carouselsPagination.isLoading = false;
          state.carouselsPagination.error = "";
          state.tilePagination = action.payload.tilePagination;
          state.refreshIntervalSecs = action.payload.refreshIntervalSecs;
          state.lastFetchTimestamp = Date.now();
        }
      )
      .addCase(loadPlayerCarouselWithRetry.rejected, (state, action): void => {
        // Ignore abort-driven rejections (empty message = intentional cancel)
        const message: string =
          action.payload !== null && action.payload !== undefined
            ? action.payload.message
            : UNKNOWN_ERROR_MESSAGE;
        if (message === "") {
          state.carouselsPagination.isLoading = false;
          return;
        }
        state.carouselsPagination.isLoading = false;
        state.carouselsPagination.error = message;
      });

    // Load more tiles for a specific rail (append)
    builder
      .addCase(loadMorePlayerCarouselTiles.pending, (state, action): void => {
        const tileState = state.tilePagination[action.meta.arg.carouselId];
        if (tileState !== undefined) {
          tileState.isLoading = true;
          tileState.error = "";
        }
      })
      .addCase(
        loadMorePlayerCarouselTiles.fulfilled,
        (state, action: PayloadAction<TLoadMoreTilesPayload>): void => {
          const tileState = state.tilePagination[action.payload.carouselId];
          if (tileState !== undefined) {
            applyOffsetPage(tileState, action.payload.page);
          }
        }
      )
      .addCase(loadMorePlayerCarouselTiles.rejected, (state, action): void => {
        const tileState = state.tilePagination[action.meta.arg.carouselId];
        if (tileState !== undefined) {
          tileState.isLoading = false;
          tileState.error =
            action.payload === undefined
              ? UNKNOWN_ERROR_MESSAGE
              : action.payload;
        }
      });

    // Reset carousel state when a new playback session starts
    builder.addCase(playableUpdated, (): PlayerCarouselState => {
      return INITIAL_STATE;
    });
  }
});

export const {
  setFocusedPlayerCarouselIndex,
  setFocusedPlayerCarouselTileIndex,
  setPlayerCarouselFocusedField,
  clearPlayerCarouselError,
  playerCarouselReset
} = playerCarouselSlice.actions;

// ─── Selectors ───

export const selectPlayerCarousel = (state: UnityRootState): TCarouselRail[] =>
  state.playerCarousel.carouselsPagination.items;

export const selectIsLoadingPlayerCarousel = (state: UnityRootState): boolean =>
  state.playerCarousel.carouselsPagination.isLoading;

export const selectPlayerCarouselError = (state: UnityRootState): string =>
  state.playerCarousel.carouselsPagination.error;

export const selectPlayerCarouselPagination = (
  state: UnityRootState
): TCarouselRailPaginationState => state.playerCarousel.carouselsPagination;

export const selectIsLoadingMorePlayerCarousel = (
  state: UnityRootState
): boolean => {
  const p = state.playerCarousel.carouselsPagination;
  return p.isLoading && p.items.length > 0;
};

export const selectPlayerCarouselTilePagination =
  (carouselId: string) =>
  (state: UnityRootState): TTilePaginationState | undefined =>
    state.playerCarousel.tilePagination[carouselId];

export const selectAllPlayerCarouselTilePagination = (
  state: UnityRootState
): Record<string, TTilePaginationState> => state.playerCarousel.tilePagination;

export const selectFocusedPlayerCarouselIndex = (
  state: UnityRootState
): number => state.playerCarousel.focusedCarouselIndex;

export const selectFocusedPlayerCarouselTileIndices = (
  state: UnityRootState
): Partial<Record<number, number>> => state.playerCarousel.focusedTileIndices;

export const selectPlayerCarouselFocusedField = (
  state: UnityRootState
): PlayerCarouselFocusedField => state.playerCarousel.focusedField;

export const selectPlayerCarouselRefreshIntervalSecs = (
  state: UnityRootState
): number => state.playerCarousel.refreshIntervalSecs;

export const selectPlayerCarouselLastFetchTimestamp = (
  state: UnityRootState
): number => state.playerCarousel.lastFetchTimestamp;

export const playerCarouselReducer = playerCarouselSlice.reducer;
export default playerCarouselSlice.reducer;
